import { Suspense, useEffect, useLayoutEffect, useRef } from 'react';

import { Box, Center, Spinner } from '@frontend/legacy-components';

import { GenericError } from 'modules/common/components/errorBoundary/GenericError';

import { DxfSceneCanvas } from './DxfSceneCanvasLazy';
import { useDxfViewerSession } from './DxfViewerContext';
import { DxfDetailsOverlay } from './components/DxfDetailsOverlay';
import { DxfRotateControls } from './components/DxfRotateControls';
import { ToolLayer } from './components/ToolLayer';
import {
  DXF_CANVAS_BACKGROUND,
  MONOCHROME_COLOR,
  TEXT_LAYER_COLOR,
} from './constants';
import { useDxfAxisDimensionsOverlay } from './hooks/useDxfAxisDimensionsOverlay';
import { useDxfDetailsOverlay } from './hooks/useDxfDetailsOverlay';
import { useDxfMarqueeOverlay } from './hooks/useDxfMarqueeOverlay';
import { useDxfMeasureOverlay } from './hooks/useDxfMeasureOverlay';
import { useDxfMeasurementFormat } from './hooks/useDxfMeasurementFormat';
import { useDxfOptimalBoxOverlay } from './hooks/useDxfOptimalBoxOverlay';
import { useDxfRotateControls } from './hooks/useDxfRotateControls';
import { useDxfSceneLoad } from './hooks/useDxfSceneLoad';
import { useDxfSceneUnits } from './hooks/useDxfSceneUnits';
import { useDxfTextOverlay } from './hooks/useDxfTextOverlay';
import type { DrawingModel, DxfViewerSession } from './session/types';
import { useDxfLoad } from './session/useSessionState';
import type { MeasurementFormat } from './utils/formatMeasurement';

type DXFViewerProps = {
  /** Presigned scene-cache URL, or `null` when this viewer is not the active kind. */
  sceneUrl: string | null;
  /**
   * Resolved `$INSUNITS` from `PartFile.dxfSceneUnits`. `null` when unknown —
   * measurements then stay unitless (`"u"`).
   */
  sceneUnits: number | null;
  /** Hidden rather than unmounted when another viewer is active. */
  isPaused: boolean;
  /** Render the text layer over the geometry. */
  isTextVisible?: boolean;
};

type DxfReadyViewProps = {
  session: DxfViewerSession;
  model: DrawingModel;
  isPaused: boolean;
  format: MeasurementFormat;
};

/**
 * Canvas, rotate, details — anything that needs a derived drawing. Overlay
 * *hosts* stay in the shell so their refs bind on first paint. Units are
 * published from the shell (`sceneUnits` from the PartFile), not from the
 * scene blob.
 */
const DxfReadyView = ({
  session,
  model,
  isPaused,
  format,
}: DxfReadyViewProps) => {
  const rotateControlsProps = useDxfRotateControls(session);
  const details = useDxfDetailsOverlay(session, model, format);

  return (
    <>
      <Suspense fallback={null}>
        <DxfSceneCanvas
          onAttach={session.attachRenderer}
          backgroundColor={DXF_CANVAS_BACKGROUND}
          monochromeColor={MONOCHROME_COLOR}
        />
      </Suspense>
      {!isPaused && (
        <>
          <DxfRotateControls {...rotateControlsProps} />
          {details && (
            <DxfDetailsOverlay
              {...details}
              position="absolute"
              bottom={2}
              right={2}
              zIndex={2}
            />
          )}
        </>
      )}
    </>
  );
};
DxfReadyView.displayName = 'DxfReadyView';

/**
 * Base component for the DXF scene viewer — the DXF equivalent of
 * `PDFViewer`/`CADViewer`.
 *
 * The shell always mounts the input surface and overlay hosts. `load.status`
 * is the only gate: spinner / error live here, and a ready drawing mounts
 * `DxfReadyView`. Panning and zooming do not re-render this — the renderer
 * and text layer subscribe to the session's viewport store directly.
 *
 * Unlike PDFTron and the CAD viewer, this viewer is cheap and safe to unmount, so
 * it does not need the mount-latch treatment in `Viewer.tsx` — `isPaused` exists
 * for parity and to keep viewport and layer state across a brief switch away.
 *
 * Everything here is three-free. Geometry is drawn by `DxfSceneCanvas`, which is
 * lazily loaded (see `DxfSceneCanvasLazy`). `scene.texts` is a real DOM layer
 * rather than glyph geometry, by backend design.
 */
export const DXFViewer = ({
  sceneUrl,
  sceneUnits,
  isPaused,
  isTextVisible = true,
}: DXFViewerProps) => {
  const session = useDxfViewerSession();
  const surfaceRef = useRef<HTMLDivElement>(null);
  const loadInput = useDxfSceneLoad(sceneUrl);
  const load = useDxfLoad(session);
  const readyModel = load.status === 'ready' ? load.model : null;

  // Attach before loadScene in the same layout pass so the first fit sees a
  // real container size. loadScene alone can otherwise frame against 0×0
  // (scale=1) when the scene is already cached.
  useLayoutEffect(() => {
    const host = surfaceRef.current;
    return host ? session.attachSurface(host) : undefined;
  }, [session]);

  useLayoutEffect(() => {
    session.loadScene(loadInput);
  }, [session, loadInput]);

  useEffect(() => {
    session.setPaused(isPaused);
  }, [session, isPaused]);

  useDxfSceneUnits(sceneUnits);
  const format = useDxfMeasurementFormat(sceneUnits);

  const textLayerRef = useDxfTextOverlay({
    model: session.model,
    hiddenLayers: session.hiddenLayers,
    overlays: session.overlays,
    viewport: session.viewport,
  });

  /* Marquee tool */
  const marqueeRef = useDxfMarqueeOverlay({ marquee: session.marquee });

  /* Measure tool */
  const measureRef = useDxfMeasureOverlay({
    measure: session.measure,
    tool: session.tools,
    pointer: session.pointer,
    viewport: session.viewport,
    format,
  });

  /* Axis dimensions tool */
  const axisRef = useDxfAxisDimensionsOverlay({
    model: session.model,
    hiddenLayers: session.hiddenLayers,
    overlays: session.overlays,
    viewport: session.viewport,
    format,
  });

  /* Optimal box tool */
  const optimalBoxRef = useDxfOptimalBoxOverlay({
    model: session.model,
    hiddenLayers: session.hiddenLayers,
    overlays: session.overlays,
    viewport: session.viewport,
    format,
  });

  // The container must stay mounted in every state: it is the input surface and the
  // canvas's host, and unmounting it mid-fetch would discard the WebGL context.
  return (
    <Box
      ref={surfaceRef}
      height="100%"
      width="100%"
      position="relative"
      overflow="hidden"
      bg={DXF_CANVAS_BACKGROUND}
      visibility={isPaused ? 'hidden' : 'unset'}
      /* Nothing on the drawing surface is selectable text, so a press here should
         never anchor a selection to drag out of. Permanent rather than applied
         during a gesture: by the time a gesture is recognised, the anchor is
         already placed. */
      userSelect="none"
      data-testid="dxf-viewer"
    >
      {readyModel && (
        <DxfReadyView
          session={session}
          model={readyModel}
          isPaused={isPaused}
          format={format}
        />
      )}
      {/* Overlay hosts stay mounted so hook effects can bind their refs on first
          paint — gating them on readiness left layerRef null forever. */}
      <Box
        ref={textLayerRef}
        position="absolute"
        inset={0}
        overflow="hidden"
        pointerEvents="none"
        visibility={readyModel && isTextVisible ? 'visible' : 'hidden'}
        className="dxf-text-layer"
      >
        {readyModel?.texts.map(({ id, text }) => (
          <Box
            as="span"
            key={id}
            position="absolute"
            whiteSpace="pre"
            transformOrigin="left bottom"
            lineHeight={1}
            color={TEXT_LAYER_COLOR}
            userSelect="none"
          >
            {text.s}
          </Box>
        ))}
      </Box>
      <ToolLayer ref={marqueeRef} data-testid="dxf-marquee-layer" />
      <ToolLayer ref={measureRef} data-testid="dxf-measure-layer" />
      <ToolLayer ref={axisRef} data-testid="dxf-axis-dimensions-layer" />
      <ToolLayer ref={optimalBoxRef} data-testid="dxf-optimal-box-layer" />
      {load.status === 'error' && (
        <Center position="absolute" inset={0}>
          <GenericError size="lg" height="100%" justifyContent="center" />
        </Center>
      )}
      {load.status === 'loading' && (
        <Center position="absolute" inset={0}>
          <Spinner size="xl" />
        </Center>
      )}
    </Box>
  );
};
